<template>
    <div class="listing-layout flex-column hp-10 ov-reset">
        <!-- LOADER -->
        <preloader :display="loading" class="preloader prelative" />

        <!-- HEADER -->
        <header class="flex-level mb-4">
            <!-- TITLE -->
            <h1 v-if="$slots['header'] != ''" class="m-0">
                <slot name="header"></slot>
            </h1>

            <!-- ACTIONS -->
            <div class="flex-right">
                <ul class="action-nav">
                    <li v-for="button in primaryButtons" :key="button.label">
                        <el-tooltip
                            :content="$t.__('common.labels.no_necessary_rights')"
                            placement="top"
                            :disabled="!button.disabled"
                        >
                            <a
                                v-if="button.event != undefined"
                                :class="{ 'btn btn-primary btn-disabled': button.disabled }"
                                @click="$emit(button.event)"
                            >
                                {{ button.label }}
                            </a>
                            <a
                                v-else
                                :href="button.href"
                                :class="{ 'btn btn-primary btn-disabled': button.disabled }"
                            >
                                {{ button.label }}
                            </a>
                        </el-tooltip>
                    </li>
                    <li>
                        <CompassButton
                            :label="$t.__('accounting.AgedBalance.CSV_export')"
                            :disabled="!permissions.export"
                            @click.prevent="filexport('csv')"
                        />
                    </li>
                </ul>
            </div>
        </header>
        <!-- HEADER END -->

        <!-- BODY -->
        <div class="listing prelative flex-column ov-reset mh-0 flex">
            <div class="kpis">
                <KpiList :kpis="kpis" type="listing" source="agedbalance" />
                <CompassPlainButton
                    v-if="kpis.length"
                    :label="$t.__('accounting.AgedBalance.regenerate_statistics')"
                    leftIcon="reload"
                    size="small"
                    variant="neutral"
                    @click="regen"
                />
                <CompassPlainButton
                    v-else
                    :label="$t.__('accounting.AgedBalance.reload_statistics')"
                    leftIcon="reload"
                    size="small"
                    variant="neutral"
                    @click="reload"
                />
            </div>

            <!-- LISTING TOOLS -->
            <div class="listing-tools flex-level flex-top no-shrink mb-2 flex-end">
                <!-- PAGINATION -->
                <paginator :pagination="pagination" @pagination:update="goToPage" />

                <!-- OPTIONS -->
                <el-dropdown placement="bottom-end">
                    <CompassIconButton
                        icon="settings-fill"
                        variant="default"
                        size="small"
                        :disabled="false"
                    />

                    <template #dropdown>
                        <el-dropdown-menu>
                            <el-dropdown-item>
                                <a
                                    href="javascript:;"
                                    @click="$store.commit(`${listinghash}/toggleDisplay`, 'table')"
                                    >{{ $l10n.__('Gestion des lignes') }}</a
                                >
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </template>
                </el-dropdown>
            </div>
            <!-- LISTING TOOLS END -->

            <!-- LISTING & FILTER PANEL -->
            <div class="flex-row mh-0 flex">
                <!-- DISPLAY OPTION PANEL -->
                <transition name="slide-fade">
                    <listing-display v-if="display.table" :listinghash="listinghash" />
                </transition>

                <!-- LISTING -->
                <div class="listingContainer br flex flex-column">
                    <!-- <div class="listingContent prelative"> -->

                    <!-- EMPTY RESULT -->
                    <div
                        v-if="!error && !loading && Object.keys(rows).length == 0"
                        class="placeholder"
                    >
                        <p class="acenter">
                            {{ $l10n.__('Aucun résultat ne correspond aux filtres appliqués') }}
                        </p>
                        <CompassButton
                            :label="$t.__('common.actions.reset')"
                            @click="$store.dispatch(`${listinghash}/resetListing`)"
                        />
                    </div>

                    <!-- ERROR -->
                    <div v-if="error" class="placeholder">
                        <p class="acenter">
                            {{ $l10n.__('Une erreur est survenue, réinitialisez votre listing') }}
                        </p>
                        <CompassButton
                            :label="$t.__('common.actions.reset')"
                            @click="$store.dispatch(`${listinghash}/resetListing`)"
                        />
                    </div>

                    <!-- TABLE COMPONENT -->

                    <aged-balance-table
                        v-if="Object.keys(rows).length > 0"
                        ref="listingTable"
                        :listinghash="listinghash"
                        :rows="rows"
                        :columns="activeColumns"
                    />

                    <!-- </div> -->
                </div>
                <!-- LISTING END -->
            </div>
            <!-- LISTING  & FILTER PANEL END -->

            <export-modal
                v-if="display.export"
                :listinghash="listinghash"
                :allColumnOption="false"
                :exportType="display.exportType"
            />
        </div>
        <!-- BODY END -->
    </div>
</template>

<script>
import Paginator from '../utilities/Paginator.vue';
import AgedBalanceTable from './AgedBalanceTable.vue';
import ListingDisplay from 'vcomponents/listing/ListingDisplay.vue';
import KpiList from 'vcomponents/utilities/KpiList.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import ListingModule from 'vueDir/store/listing';
import ExportModal from 'vcomponents/listing/ExportModal.vue';
import { guid } from 'vueDir/helpers';
import _ from 'lodash';

export default {

    components: {
        Paginator,
        AgedBalanceTable,
        ListingDisplay,
        ExportModal,
        KpiList,
        Preloader,
    },
    props: {
        endpoint: {
            type: String,
            required: true,
        },
        url: {
            type: String,
            required: true,
        },
        params: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        linkedtype: {
            type: String,
        },
        prefilter: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        enableFiltering: {
            type: Boolean,
            required: false,
            default: true,
        },
        favorite: {
            required: false,
            type: Number,
            default: null,
        },
        name: {
            required: false,
            type: String,
            default: '',
        },
        filter: {
            required: false,
            type: Object,
            default() {
                return {};
            },
        },
        buttons: {
            required: false,
            type: Array,
            default() {
                return [];
            },
        },
    },

    data: function () {
        return {
            listinghash: '',
        };
    },

    computed: {
        primaryButtons() {
            const primaryButtons = this.buttons.filter((button) => {
                return button.primary;
            });

            if (primaryButtons.length) {
                return primaryButtons;
            }

            return false;
        },

        secondaryButtons() {
            const secondaryButtons = this.buttons.filter((button) => {
                return button.primary == undefined || !button.primary;
            });

            if (secondaryButtons.length) {
                return secondaryButtons;
            }

            return false;
        },

        ajax() {
            return this.$store.state[this.listinghash].ajax;
        },

        kpis() {
            return this.$store.state[this.listinghash].kpis;
        },

        error() {
            return this.$store.state[this.listinghash].error;
        },

        init() {
            return this.$store.state[this.listinghash].init;
        },

        rows() {
            return this.$store.state[this.listinghash].rows;
        },

        pagination() {
            return this.$store.state[this.listinghash].pagination;
        },
        columns() {
            return this.$store.state[this.listinghash].columns;
        },
        display() {
            return this.$store.state[this.listinghash].display;
        },

        activeColumns() {
            return this.$store.getters[`${this.listinghash}/activeColumns`];
        },

        activeFilters() {
            return this.$store.getters[`${this.listinghash}/activeFilters`];
        },

        loading() {
            return !!this.ajax;
        },

        meta() {
            return this.$store.getters[`${this.listinghash}/meta`];
        },

        permissions() {
            return this.$store.state[this.listinghash].permissions;
        },

        favoriteData() {
            return this.$store.state[this.listinghash].favoriteData;
        },

        kpis() {
            return this.$store.state[this.listinghash].kpis;
        },
    },

    created() {
        this.registerStore();

        // Listing init
        this.$store.dispatch(`${this.listinghash}/init`, this.$props).then(() => this.fetch());
        EventBus.on('listing:refresh', this.fetch);
    },
    unmounted() {
        EventBus.off('listing:refresh', this.fetch);
    },
    methods: {
        registerStore() {
            // Dynamically require state
            this.listinghash = this.name + '_' + guid();
            const store = this.$store;

            if (!(store && store.state && store.state[this.listinghash])) {
                store.registerModule(this.listinghash, ListingModule);
            }
        },

        filexport(format) {
            if (this.permissions.export) {
                this.$store.commit(`${this.listinghash}/toggleDisplay`, 'export');
                this.$store.commit(`${this.listinghash}/updateExportType`, format);
            } else {
                this.$store.dispatch(`${this.listinghash}/permissionDenied`);
            }
        },

        // PAGINATION
        goToPage(page) {
            this.$store.commit(`${this.listinghash}/updateCurrentPage`, page);
            window.EventBus.emit('listing:updatePage', { listing: this.listinghash, page: page });
            this.fetch();
        },

        // Main methods
        fetch(overwriteMeta) {
            this.$store.dispatch(`${this.listinghash}/fetch`, overwriteMeta);
        },

        reload() {
            this.$store.dispatch(`${this.listinghash}/reloadKpis`);
        },

        regen() {
            // Call endpoint to clear
            SellsyJS.WebServiceCaller.del(`/${this.linkedtype}/kpis`, {}, () =>
                this.$store.commit(`${this.listinghash}/updateKpis`, []),
            );
        },
    },
};
</script>

<style lang="sass" scoped>
@import '~@sellsy/styleguide/src/_transitions';

.flex-row {
    .leftpane{
        width : 280px;
    }
}
</style>
